import { useEffect } from 'react'
import {
    FaUser, FaIdCard, FaTag, FaBuilding, FaStore,
    FaMapMarkerAlt, FaRoad, FaCity, FaMailBulk, FaEnvelope,
    FaAddressBook, FaPhoneAlt, FaStickyNote, FaInfoCircle,
    FaFileInvoice, FaClipboardCheck,
} from 'react-icons/fa'
import { formatCNPJ } from '../../../shared/functions/cnpjFormat'
import './ClientSummaryModal.css'

const IE_INDICATOR_LABELS = {
    contribuinte: 'Contribuinte de ICMS',
    isento: 'Contribuinte isento de IE',
    nao_contribuinte: 'Não contribuinte',
}

const Field = ({ icon: Icon, label, value }) => (
    <div className="csm-field">
        <span className="csm-field-label">
            {Icon && <Icon size={11} />}
            {label}
        </span>
        <span className="csm-field-value">{value || '—'}</span>
    </div>
)

function ClientSummaryModal({ client, onClose }) {

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose() }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [onClose])

    if (!client) return null

    const contacts = client.contacts || []

    return (
        <div className="csm-overlay" onClick={onClose}>
            <div className="csm-box" onClick={(e) => e.stopPropagation()}>

                <button className="csm-close shd-modal-close" onClick={onClose}>X</button>

                {/* ── Header ── */}
                <div className="csm-header">
                    <div className="csm-header-top">
                        <div className="csm-header-icon"><FaUser /></div>
                        <div>
                            <p className="csm-name">{client.name}</p>
                            <span className="csm-accent" />
                        </div>
                    </div>
                    <div className="csm-header-bottom">
                        <span className={client.disabled ? 'csm-badge-disabled' : 'csm-badge-active'}>
                            {client.disabled ? 'Desabilitado' : 'Ativo'}
                        </span>
                    </div>
                </div>

                {/* ── Body ── */}
                <div className="csm-body">

                    <div className="csm-section">
                        <div className="csm-section-header">
                            <FaInfoCircle size={13} />
                            <span>Identificação</span>
                        </div>
                        <div className="csm-grid">
                            <Field icon={FaTag} label="Tipo" value={client.personType === 'juridica' ? 'Pessoa Jurídica' : 'Pessoa Física'} />
                            {client.personType === 'juridica' ? (
                                <>
                                    <Field icon={FaBuilding} label="Razão Social" value={client.corporateName} />
                                    <Field icon={FaStore} label="Nome Fantasia" value={client.fantasyName} />
                                    <Field icon={FaIdCard} label="CNPJ" value={formatCNPJ(client.CNPJ)} />
                                </>
                            ) : (
                                <>
                                    <Field icon={FaUser} label="Nome Completo" value={client.completeName} />
                                    <Field icon={FaIdCard} label="CPF" value={client.CPF} />
                                </>
                            )}
                        </div>
                    </div>

                    <div className="csm-section">
                        <div className="csm-section-header">
                            <FaMapMarkerAlt size={13} />
                            <span>Endereço e Contato</span>
                        </div>
                        <div className="csm-grid">
                            <Field
                                icon={FaMapMarkerAlt}
                                label="Endereço"
                                value={[client.address, client.number].filter(Boolean).join(', ') + (client.complement ? ` — ${client.complement}` : '')}
                            />
                            <Field icon={FaRoad} label="Bairro" value={client.district} />
                            <Field icon={FaCity} label="Cidade/UF" value={[client.city, client.state].filter(Boolean).join('/')} />
                            <Field icon={FaMailBulk} label="CEP" value={client.cep} />
                            <Field icon={FaEnvelope} label="Email" value={client.email} />
                        </div>
                    </div>

                    <div className="csm-section">
                        <div className="csm-section-header">
                            <FaAddressBook size={13} />
                            <span>Contatos</span>
                        </div>
                        {contacts.length > 0 ? (
                            <div className="csm-list">
                                {contacts.map((c, i) => (
                                    <div key={i} className="csm-list-row">
                                        <span className="csm-list-row-icon"><FaAddressBook size={12} /></span>
                                        <span className="csm-list-row-label">{c.name}</span>
                                        <span className="csm-list-row-value"><FaPhoneAlt size={10} />{c.phone}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="csm-empty">Nenhum contato cadastrado.</p>
                        )}
                    </div>

                    <div className="csm-section">
                        <div className="csm-section-header">
                            <FaFileInvoice size={13} />
                            <span>Dados Fiscais</span>
                        </div>
                        <div className="csm-grid">
                            <Field icon={FaFileInvoice} label="Inscrição Estadual" value={client.stateRegistration} />
                            <Field icon={FaClipboardCheck} label="Indicador de IE" value={IE_INDICATOR_LABELS[client.ieIndicator]} />
                        </div>
                    </div>

                    {client.obs && (
                        <div className="csm-section">
                            <div className="csm-section-header">
                                <FaStickyNote size={13} />
                                <span>Observações</span>
                            </div>
                            <p className="csm-description">{client.obs}</p>
                        </div>
                    )}

                </div>

                <div className="csm-footer" />
            </div>
        </div>
    )
}

export default ClientSummaryModal
